import React, { useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSprings, animated } from 'react-spring';
import { useGesture } from 'react-use-gesture';

import clamp from 'lodash/clamp';
import queryString from 'query-string';

import Button from 'components/Buttons/Button';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import Stepper from 'components/Stepper';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { TravelerUserFromInvite } from 'models/travelerUser';
import { htmlT } from 'utils/generic';

import Step from './components/Step';
import {
  Container,
  ContainerDesktop,
  ButtonDesktopContainer,
  StepContainerDesktop,
  Steps,
  Circle,
  StyledGallery,
  AnimationContainer,
  IconArea,
} from './styles';

interface Props {
  invitedId: string;
  userInvited?: TravelerUserFromInvite;
  isLoading: boolean;
  isError: boolean;
}

const Onboarding = ({ isLoading }: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const index = useRef(0);
  const [step, setStep] = useState(0);
  const user = useUserAccount();

  const queryParams = queryString.parse(location.search) as {
    email?: string;
    name?: string;
    lastName?: string;
  };

  const steps = [
    {
      id: 0,
      iconPath: '/images/onboarding-01.svg',
      text: htmlT(t, 'onboarding.firstStep.text'),
      marginLeft: true,
      marginRigth: false,
    },
    {
      id: 1,
      iconPath: '/images/onboarding-02.svg',
      text: htmlT(t, 'onboarding.secondStep.text'),
      marginLeft: true,
      marginRigth: true,
    },
    {
      id: 2,
      iconPath: '/images/onboarding-03.svg',
      text: htmlT(t, 'onboarding.thirdStep.text'),
      marginLeft: false,
      marginRigth: true,
    },
  ];
  const [springProps, setSpringProps] = useSprings<{ x: number; sc: number; display: 'block' | 'none' }>(
    steps.length,
    (i) => ({
      x: i * window.innerWidth,
      sc: 1,
      display: 'block',
    }),
  );

  const nextStep = () => {
    if (user?.type === 'admin') {
      history.push(`/welcome?email=${queryParams.email}&name=${queryParams.name}&lastName=${queryParams.lastName}`);
    } else {
      history.push('/dashboard');
    }
  };

  const goToStep = () => {
    if (user?.type === 'admin') {
      history.push(`/welcome?email=${queryParams.email}&name=${queryParams.name}&lastName=${queryParams.lastName}`);
    } else {
      history.push('/dashboard');
    }
  };

  const bind = useGesture(({ down, delta: [xDelta], direction: [xDir], distance, cancel }) => {
    const actualIndex = index.current;

    if (down && distance > window.innerWidth / 4) {
      if (step === steps.length - 1 && xDir < 0) nextStep();

      if (cancel) cancel();
      index.current = clamp(index.current + (xDir > 0 ? -1 : 1), 0, steps.length - 1);
    }

    setStep(actualIndex);

    setSpringProps((i: any) => {
      if (i < index.current - 1 || i > index.current + 1) return { display: 'none' };
      const x = (i - index.current) * window.innerWidth + (down ? xDelta : 0);
      const sc = down ? 1 - distance / window.innerWidth / 2 : 1;
      return { x, sc, display: 'block' };
    });
  });

  const handleSelect = useCallback((i: number) => {
    setStep(i);
  }, []);

  return isLoading ? (
    <Loader fullScreen />
  ) : (
    <>
      <Desktop>
        <ContainerDesktop>
          <IconArea>
            <IconArea.Logo src="/images/logo-rappi-travel4business.svg" />
          </IconArea>
          <StyledGallery enableKeyboard={false} index={step} onRequestChange={handleSelect}>
            {steps.map((step) => (
              <StepContainerDesktop key={step.id}>
                <StepContainerDesktop.TextArea>{step.text && step.text}</StepContainerDesktop.TextArea>
                <StepContainerDesktop.Image marginRigth={step.marginRigth}>
                  <img src={step.iconPath} alt=""></img>
                </StepContainerDesktop.Image>
              </StepContainerDesktop>
            ))}
          </StyledGallery>
          <Steps>
            {steps.map((_: any, i: number) => (
              <Circle onClick={() => setStep(i)} key={i} styledProps={{ active: i === step }} />
            ))}
          </Steps>
          <ButtonDesktopContainer>
            <Button onClick={goToStep}>{t('walkThrough.next')}</Button>
          </ButtonDesktopContainer>
        </ContainerDesktop>
      </Desktop>

      <Mobile>
        <Container>
          <Stepper index={step} onJump={goToStep} stepsProps={steps}>
            <AnimationContainer>
              {springProps.map(({ x, display, sc }, i) => (
                <animated.div
                  {...bind()}
                  key={i}
                  style={{ display, transform: x.interpolate((x) => `translate3d(${x}px, 0, 0)`) }}
                >
                  <animated.div style={{ transform: sc.interpolate((s) => `scale(${s})`) }}>
                    <Step iconPath={steps[i].iconPath} html={steps[i].text} marginLeft={steps[i].marginLeft} />
                  </animated.div>
                </animated.div>
              ))}
            </AnimationContainer>
          </Stepper>
        </Container>
      </Mobile>
    </>
  );
};

export default Onboarding;
